import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';
import { Message, ChatRole, OnboardingData } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { Logger } from '../utils/logger';
import Config from '../config/config';

const API_URL = Config.getInstance().apiUrl;

export function usePositioning() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [positioningContext, setPositioningContext] = useState<string>('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSavingPositioning, setIsSavingPositioning] = useState(false);
  const { t } = useLanguage();
  // Keep a ref so fetchHistory can read the current translation without being recreated on language change
  const initialMessageRef = useRef(t.positioning.chatInitialMessage);
  useEffect(() => {
    initialMessageRef.current = t.positioning.chatInitialMessage;
  }, [t.positioning.chatInitialMessage]);

  // V4-POSITIONING-DOUBLE-PUT (2026-07-03, root cause confirmée) — le bouton
  // "Enregistrer" (PositioningToolbar → AnimatedIconButton) n'était jamais
  // disabled/loading pendant la sauvegarde : un double-clic (ou un 2e clic
  // avant le re-render qui masque le bouton une fois hasUnsavedChanges=false)
  // déclenchait 2 PUT /positioning/ identiques. Double protection : (1) ce
  // dedup in-flight coalesce les appels à contenu identique tant que le
  // premier est en vol, (2) `isSavingPositioning` ci-dessous désactive le
  // bouton dans l'UI pour supprimer la fenêtre de double-clic à la source.
  const inFlightSaveRef = useRef<{ key: string; promise: Promise<unknown> } | null>(null);

  // Fetch both positioning context and history
  // Fix V4-AGENCY-3 (2026-05-21) : on envoie `space_id` en query param pour
  // éviter le data leak côté agences multi-spaces (le backend fallback
  // sélectionnait "first owned space" sans filtre, donnant le positionnement
  // d'un autre space à l'écran courant).
  const fetchPositioning = useCallback(async (spaceId?: string) => {
    setIsLoadingContext(true);
    try {
      const url = spaceId ? `/positioning/?space_id=${encodeURIComponent(spaceId)}` : '/positioning/';
      const response = await apiClient.get(url);
      if (response.data?.positioning_context) {
        setPositioningContext(response.data.positioning_context);
      }
      // NE PAS charger l'historique si c'est un template - on veut démarrer l'onboarding
      if (!response.data?.is_template && response.data?.positioning_history && response.data.positioning_history.length > 0) {
        // V4-FRONT-ANY-USAGE 2026-05-28 — backend renvoie { role, content, isTyping? } (cf. routes/positioning.py)
        setMessages(response.data.positioning_history.map((msg: Omit<Message, 'id'>, index: number) => ({
          ...msg,
          id: `hist-${index}`
        })));
      }
      // Retourner les données pour permettre à l'appelant de vérifier is_template
      return response.data;
    } catch (error) {
      Logger.error('Failed to fetch positioning:', error);
      return null;
    } finally {
      setIsLoadingContext(false);
    }
  }, []);

  // V4-AGENCY-3-OTHER (2026-05-21) : `spaceId` ciblé pour éviter de lire
  // l'historique du premier space owned (data leak agence).
  const fetchHistory = useCallback(async (spaceId?: string) => {
    setIsLoadingHistory(true);
    try {
      const url = spaceId
        ? `/positioning/history?space_id=${encodeURIComponent(spaceId)}`
        : '/positioning/history';
      const response = await apiClient.get(url);
      if (Array.isArray(response.data) && response.data.length > 0) {
        // V4-FRONT-ANY-USAGE 2026-05-28
        setMessages(response.data.map((msg: Omit<Message, 'id'>, index: number) => ({
          ...msg,
          id: `hist-${index}`
        })));
      } else {
        // Initial message if no history
        setMessages([{
          id: '1',
          role: ChatRole.ASSISTANT,
          content: initialMessageRef.current,
        }]);
      }
    } catch (error) {
      Logger.error('Failed to fetch positioning history:', error);
      // Fallback to initial message
      setMessages([{
        id: '1',
        role: ChatRole.ASSISTANT,
        content: initialMessageRef.current,
      }]);
    } finally {
      setIsLoadingHistory(false);
    }
  // initialMessageRef is a ref — stable, no need to list as dep

  }, []);

  // V4-AGENCY-3-OTHER (2026-05-21) : `spaceId` est désormais envoyé dans le
  // body pour cibler explicitement le space concerné côté backend (sinon leak
  // sur les comptes agence multi-spaces).
  const sendMessage = async (content: string, currentMessages: Message[], spaceId?: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: ChatRole.USER,
      content: content.trim(),
    };

    const newMessages = [...currentMessages, userMessage];

    // Return the new state immediately for optimistic update
    // The caller should update the state

    try {
      const payload: Record<string, unknown> = {
        messages: newMessages.map(m => ({ role: m.role, content: m.content }))
      };
      if (spaceId) payload.space_id = spaceId;
      const response = await apiClient.post('/positioning/chat', payload);

      return {
        success: true,
        userMessage,
        assistantContent: response.data.message.content
      };
    } catch (error) {
      Logger.error('Error sending message:', error);
      return {
        success: false,
        userMessage,
        error
      };
    }
  };

  /**
   * Send message with SSE streaming response.
   * Returns the user message immediately, then streams the assistant response.
   * @param content User message content
   * @param currentMessages Current messages array
   * @param onChunk Callback called with each content chunk
   * @param onDone Callback called when streaming is complete with full response
   */
  const sendMessageStream = async (
    content: string,
    currentMessages: Message[],
    onChunk: (chunk: string) => void,
    onDone: (fullContent: string) => void,
    onError?: (error: Error) => void,
    spaceId?: string,
  ) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      role: ChatRole.USER,
      content: content.trim(),
    };

    const newMessages = [...currentMessages, userMessage];
    const token = authService.getToken(); // Use authService to check expiration

    if (!token) {
      onError?.(new Error('Authentication token expired. Please login again.'));
      setIsStreaming(false);
      return;
    }

    setIsStreaming(true);

    try {
      // V4-AGENCY-3-OTHER (2026-05-21) : envoi de space_id dans le body pour
      // éviter le fallback "first owned space" côté backend (data leak agence).
      const body: Record<string, unknown> = {
        messages: newMessages.map(m => ({ role: m.role, content: m.content }))
      };
      if (spaceId) body.space_id = spaceId;
      const response = await fetch(`${API_URL}/positioning/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);

          // V4-FRONT-SSE-ERROR-SWALLOWED — on isole le JSON.parse dans son PROPRE
          // try/catch (pour tolérer les chunks SSE incomplets) et on sort le
          // handling métier de ce try. Avant, le `throw` sur un event d'erreur
          // typé était intercepté par le catch de parse et downgradé en
          // Logger.debug → l'erreur ne remontait JAMAIS à onError (UI muette).
          let data: { error?: unknown; message?: unknown; content?: unknown; done?: unknown };
          try {
            data = JSON.parse(payload);
          } catch (e) {
            // Vrai parse error (chunk incomplet) — à ignorer en debug.
            if (payload.trim()) {
              Logger.debug('SSE parse warning:', e);
            }
            continue;
          }

          // Parse réussi : le throw ci-dessous n'est plus avalé, il remonte au
          // catch externe (ligne ~249) qui route vers onError.
          // Erreur SSE : shape typé {error: <code>, message: <FR>} (V4-ARCH-TYPED-ERRORS)
          // OU legacy {error: <message string>}. On préfère `message` (lisible) et
          // on retombe sur `error` string quand le backend n'a pas encore migré.
          if (data.error || data.message) {
            const readable =
              (typeof data.message === 'string' && data.message) ||
              (typeof data.error === 'string' && data.error) ||
              'La génération a échoué.';
            throw new Error(readable);
          }

          if (data.content) {
            fullContent += data.content as string;
            onChunk(data.content as string);
          }

          if (data.done) {
            onDone(fullContent);
          }
        }
      }

      return {
        success: true,
        userMessage,
        assistantContent: fullContent
      };
    } catch (error) {
      Logger.error('Error in streaming message:', error);
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
      return {
        success: false,
        userMessage,
        error
      };
    } finally {
      setIsStreaming(false);
    }
  };

  // ==========================================================================
  // NEW METHODS: Template, URL Extraction, Style Analysis, Diff
  // ==========================================================================

  /**
   * Get the default positioning template
   */
  const getTemplate = useCallback(async () => {
    try {
      const response = await apiClient.get('/positioning/template');
      return response.data;
    } catch (error) {
      Logger.error('Failed to get template:', error);
      throw error;
    }
  }, []);

  /**
   * Extract positioning from a URL.
   * V4-AGENCY-3-OTHER (2026-05-21) : `spaceId` permet au backend de persister
   * l'URL sur le bon space et d'enqueue brand extraction.
   */
  const extractFromUrl = useCallback(async (url: string, spaceId?: string) => {
    try {
      const payload: Record<string, unknown> = { url };
      if (spaceId) payload.space_id = spaceId;
      const response = await apiClient.post('/positioning/extract-from-url', payload, { timeout: 90000 });
      return response.data;
    } catch (error) {
      Logger.error('Failed to extract from URL:', error);
      throw error;
    }
  }, []);

  /**
   * V4-ONBOARDING-PERSONAL-BRANDING — Extract positioning from the user's own
   * posts (personal-branding path of the express setup, no website).
   * Returns the same shape as extractFromUrl ({ extracted, merged_template }).
   * When `spaceId` is provided, the backend persists the structured positioning
   * AND the raw posts as few-shot style examples.
   */
  const extractFromPosts = useCallback(async (posts: string[], spaceId?: string, sectorWatch?: boolean) => {
    try {
      const payload: Record<string, unknown> = { posts };
      if (spaceId) payload.space_id = spaceId;
      // V4-ONBOARDING-CHECKLIST-OPTIONAL — persiste le choix de veille sectorielle
      // (false ⇒ thématiques/sources deviennent optionnelles dans la checklist).
      if (sectorWatch !== undefined) payload.sector_watch = sectorWatch;
      const response = await apiClient.post('/positioning/extract-from-posts', payload, { timeout: 90000 });
      return response.data;
    } catch (error) {
      Logger.error('Failed to extract from posts:', error);
      throw error;
    }
  }, []);

  /**
   * V4-ONBOARDING-PB-CONNECTED-IMPORT — Best-effort : récupère le texte des posts
   * récents gérés via Zernio pour un compte connecté, afin de préremplir le
   * textarea « personal branding ». Ne lit PAS l'historique organique du profil.
   * Renvoie toujours `{ posts, source }` (posts=[] si rien à importer).
   */
  const fetchConnectedPosts = useCallback(async (
    spaceId: string,
    limit = 5,
  ): Promise<{ posts: string[]; source: { network?: string; username?: string } | null }> => {
    try {
      const response = await apiClient.get(
        `/positioning/connected-posts?space_id=${encodeURIComponent(spaceId)}&limit=${limit}`,
      );
      return {
        posts: Array.isArray(response.data?.posts) ? response.data.posts : [],
        source: response.data?.source ?? null,
      };
    } catch (error) {
      Logger.warn('Failed to fetch connected posts:', error);
      return { posts: [], source: null };
    }
  }, []);

  /**
   * Analyze writing style from example posts
   */
  const analyzeStyle = useCallback(async (posts: string[]) => {
    try {
      const response = await apiClient.post('/positioning/analyze-style', { posts });
      return response.data;
    } catch (error) {
      Logger.error('Failed to analyze style:', error);
      throw error;
    }
  }, []);

  /**
   * Suggest changes to positioning based on instruction.
   * V4-AGENCY-3-OTHER (2026-05-21) : `spaceId` cible explicitement le space
   * source (sinon fallback first-owned côté backend → leak agence).
   */
  const suggestChanges = useCallback(async (
    instruction: string,
    currentPositioning?: string,
    spaceId?: string,
  ) => {
    try {
      const payload: Record<string, unknown> = {
        instruction,
        current_positioning: currentPositioning
      };
      if (spaceId) payload.space_id = spaceId;
      const response = await apiClient.post('/positioning/suggest-changes', payload);
      return response.data;
    } catch (error) {
      Logger.error('Failed to suggest changes:', error);
      throw error;
    }
  }, []);

  /**
   * Apply suggested changes to positioning.
   * V4-AGENCY-3-OTHER (2026-05-21) : `spaceId` cible explicitement le space à
   * écrire (write-side bug sinon).
   */
  const applyChanges = useCallback(async (
    newPositioning: string,
    source: string = 'ai_suggestion',
    spaceId?: string,
  ) => {
    try {
      const payload: Record<string, unknown> = {
        new_positioning: newPositioning,
        source
      };
      if (spaceId) payload.space_id = spaceId;
      const response = await apiClient.post('/positioning/apply-changes', payload);
      if (response.data?.positioning_context) {
        setPositioningContext(response.data.positioning_context);
      }
      return response.data;
    } catch (error) {
      Logger.error('Failed to apply changes:', error);
      throw error;
    }
  }, []);

  /**
   * Get version history for a given space.
   * V4-AGENCY-3-OTHER (2026-05-21) : `spaceId` en query param pour ne plus
   * retourner les versions du premier space owned.
   */
  const getVersions = useCallback(async (spaceId?: string) => {
    try {
      const url = spaceId
        ? `/positioning/versions?space_id=${encodeURIComponent(spaceId)}`
        : '/positioning/versions';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      Logger.error('Failed to get versions:', error);
      throw error;
    }
  }, []);

  /**
   * Restore a specific version on a given space.
   * V4-AGENCY-3-OTHER (2026-05-21) : `spaceId` cible le space à écrire.
   */
  const restoreVersion = useCallback(async (versionId: string, spaceId?: string) => {
    try {
      const url = spaceId
        ? `/positioning/restore-version/${versionId}?space_id=${encodeURIComponent(spaceId)}`
        : `/positioning/restore-version/${versionId}`;
      const response = await apiClient.post(url);
      if (response.data?.positioning_context) {
        setPositioningContext(response.data.positioning_context);
      }
      return response.data;
    } catch (error) {
      Logger.error('Failed to restore version:', error);
      throw error;
    }
  }, []);

  /**
   * Update positioning directly (text mode).
   *
   * V4-AGENCY-1 (2026-05-21) : on envoie systématiquement `space_id` quand
   * il est disponible côté contexte d'auth. Évite que le backend "devine"
   * un space sur un compte agence multi-spaces, ce qui pouvait écrire le
   * positionnement sur le mauvais space.
   *
   * `spaceId` est optionnel pour ne pas casser les rares appels hors-contexte
   * (legacy) — le backend log un warning si absent.
   */
  const updatePositioning = useCallback(async (
    content: string,
    source: string = 'manual',
    spaceId?: string,
  ) => {
    // V4-POSITIONING-DOUBLE-PUT — coalesce un save identique déjà en vol.
    const key = `${spaceId ?? ''}::${source}::${content}`;
    const existing = inFlightSaveRef.current;
    if (existing && existing.key === key) {
      Logger.warn('[usePositioning] Duplicate save coalesced (same content in flight)');
      return existing.promise;
    }

    const run = (async () => {
      setIsSavingPositioning(true);
      try {
        const payload: Record<string, unknown> = {
          positioning_context: content,
          source,
        };
        if (spaceId) payload.space_id = spaceId;
        const response = await apiClient.put('/positioning/', payload);
        if (response.data?.positioning_context) {
          setPositioningContext(response.data.positioning_context);
        }
        return response.data;
      } catch (error) {
        Logger.error('Failed to update positioning:', error);
        throw error;
      } finally {
        if (inFlightSaveRef.current?.key === key) {
          inFlightSaveRef.current = null;
        }
        setIsSavingPositioning(false);
      }
    })();

    inFlightSaveRef.current = { key, promise: run };
    return run;
  }, []);

  /**
   * Enrich onboarding data into a professional positioning prompt using LLM.
   * This endpoint handles caching automatically on the backend.
   *
   * @param onboardingData - The collected onboarding data
   * @param language - Language code (fr, en, es)
   * @returns Object with { positioning: string, cached: boolean }
   */
  const enrichPositioning = useCallback(async (
    onboardingData: OnboardingData,
    language: string = 'fr'
  ): Promise<{ positioning: string; cached: boolean }> => {
    try {
      const response = await apiClient.post('/positioning/enrich', {
        onboarding_data: onboardingData,
        language
      }, { timeout: 90000 });
      return {
        positioning: response.data.positioning,
        cached: response.data.cached || false
      };
    } catch (error) {
      Logger.error('Failed to enrich positioning:', error);
      throw error;
    }
  }, []);

  return {
    // State
    messages,
    setMessages,
    positioningContext,
    setPositioningContext,
    isLoadingHistory,
    isLoadingContext,
    isStreaming,
    isSavingPositioning,
    // Chat methods
    fetchHistory,
    fetchPositioning,
    sendMessage,
    sendMessageStream,
    // New methods
    getTemplate,
    extractFromUrl,
    extractFromPosts,
    fetchConnectedPosts,
    analyzeStyle,
    suggestChanges,
    applyChanges,
    getVersions,
    restoreVersion,
    updatePositioning,
    enrichPositioning
  };
}
